import { useQueryClient } from "@tanstack/react-query";
import { addError } from "@wayflyer/blackbox-fe";
import type { FundingApplicationSchemas } from "api-types";
import { usePostHog } from "posthog-js/react";
import { useRef, useState } from "react";
import { useErrorModal, useFundingApplicationMutation } from "shared-ui";

import { invalidateBankingSourceQueries, useSetCoverageAcknowledgement } from "../../../queries/bank-connect/queries";
import { useFundingApplication } from "../../../queries/funding-application/queries";
import {
  getNextStepDecision,
  getPreviousDeclarationChoice,
  type AggregateCoverage,
  type BankingDataDeclarationChoice,
} from "../banking-accounts/utils";

const NOT_COMPLETED_MESSAGE = "banking-data-declaration: customer did not complete the declaration";

type PendingAnswer = { resolve: () => void; reject: (error: Error) => void };

type UseBankingDataDeclarationParams = {
  customerDomain: string;
  hasProvidedBankingData: boolean;
  coverageKind: AggregateCoverage["kind"];
  hasAccountsToReview: boolean;
  hasGroupsToReview: boolean;
  groupUuidsToAcknowledge: string[];
  openReviewAccountsModal: () => void;
  openModal: () => void;
  closeModal: () => void;
};

export const useBankingDataDeclaration = ({
  customerDomain,
  hasProvidedBankingData,
  coverageKind,
  hasAccountsToReview,
  hasGroupsToReview,
  groupUuidsToAcknowledge,
  openReviewAccountsModal,
  openModal,
  closeModal,
}: UseBankingDataDeclarationParams) => {
  const { data: fundingApplication } = useFundingApplication(customerDomain);
  const fundingApplicationMutation = useFundingApplicationMutation(customerDomain);
  const setCoverageAcknowledgement = useSetCoverageAcknowledgement(customerDomain);
  const queryClient = useQueryClient();
  const { openErrorModal } = useErrorModal();
  const posthog = usePostHog();
  const pendingAnswer = useRef<PendingAnswer | null>(null);
  const [hasChosenToAddMissing, setHasChosenToAddMissing] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const saveDeclaration = async (bankingDataDeclaration: FundingApplicationSchemas.BankingDataDeclaration) => {
    try {
      await fundingApplicationMutation.mutateAsync({ id: fundingApplication.id, bankingDataDeclaration });
      return true;
    } catch (e: any) {
      openErrorModal({ variant: "submit-error", error: e });
      addError(e, { message: "banking-data-declaration: save error" });
      return false;
    }
  };

  const acknowledgeGroups = async (groupUuids: string[]) => {
    try {
      await setCoverageAcknowledgement.mutateAsync({ groupUuids, acknowledged: true });
      return true;
    } catch (e: any) {
      openErrorModal({ variant: "submit-error", error: e });
      addError(e, { message: "banking-data-declaration: acknowledge groups error" });
      return false;
    }
  };

  const settlePendingAnswer = (outcome: "continue" | "stay") => {
    const pending = pendingAnswer.current;
    pendingAnswer.current = null;
    if (outcome === "continue") {
      pending?.resolve();
    } else {
      pending?.reject(new Error(NOT_COMPLETED_MESSAGE));
    }
  };

  // Resolves when the customer may leave the page; rejects when they should stay.
  const ensureDeclaration = async () => {
    const decision = getNextStepDecision({
      hasProvidedBankingData,
      coverageKind,
      hasAccountsToReview,
      hasGroupsToReview,
      declaration: fundingApplication.bankingDataDeclaration,
    });

    if (decision === "review-accounts") {
      openReviewAccountsModal();
      throw new Error(NOT_COMPLETED_MESSAGE);
    }
    if (decision === "record-detected" && !(await saveDeclaration("full_period_detected"))) {
      throw new Error(NOT_COMPLETED_MESSAGE);
    }
    if (decision === "record-cannot-provide" && !(await saveDeclaration("cannot_provide"))) {
      throw new Error(NOT_COMPLETED_MESSAGE);
    }
    if (decision === "ask") {
      settlePendingAnswer("stay");
      await new Promise<void>((resolve, reject) => {
        pendingAnswer.current = { resolve, reject };
        openModal();
      });
    }
  };

  const onConfirm = async (choice: BankingDataDeclarationChoice) => {
    if (choice === "will_add_missing") {
      posthog?.capture("onboarding_bank_data_declaration_submitted", { choice });
      setHasChosenToAddMissing(true);
      onDismiss();
      return;
    }
    setIsConfirming(true);
    try {
      const answeredQuestion = pendingAnswer.current;
      if (!(await saveDeclaration(choice))) {
        return;
      }
      posthog?.capture("onboarding_bank_data_declaration_submitted", { choice });
      if (choice === "cannot_provide" && groupUuidsToAcknowledge.length > 0) {
        if (!(await acknowledgeGroups(groupUuidsToAcknowledge))) {
          return;
        }
        await invalidateBankingSourceQueries(queryClient, customerDomain);
      }
      // The modal may have been dismissed and reopened for a new question while this was saving.
      if (pendingAnswer.current !== answeredQuestion) {
        return;
      }
      closeModal();
      settlePendingAnswer("continue");
    } finally {
      setIsConfirming(false);
    }
  };

  const onDismiss = () => {
    closeModal();
    settlePendingAnswer("stay");
  };

  return {
    ensureDeclaration,
    onConfirm,
    onDismiss,
    shouldShowMissingPeriodBanner: hasChosenToAddMissing && hasProvidedBankingData && coverageKind === "gap",
    previousChoice: getPreviousDeclarationChoice(fundingApplication.bankingDataDeclaration),
    isSaving: isConfirming,
  };
};
